import { EditFnaStepComponent } from '../../editFnaPage/types';
import {
  FnaBuyAndSellParty,
  FnaPolicyStructure,
  MarriageType,
  NeedCapacityType,
} from '../../../common/types/analyses';
import { formatNumber } from '../../../common/components/dataGrid/dataGridEditNumberCell';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  makeAsyncOptionsColumn,
  makeBooleanColumn,
  makeEstateDutyColumns,
  makeNumberColumn,
} from '../../../common/helpers/dataGrid';
import { useFnaPartiesUpdater } from '../../hooks/useFnaPartiesUpdater';
import { useMemo } from 'react';
import { useUpdateEffect } from '../../../common/hooks/useUpdateEffect';
import DataGrid from '../../../common/components/dataGrid';
import DataGridDeleteAction from '../../../common/components/dataGrid/dataGridDeleteAction';
import DataGridEditBusinessInterestCell from '../../components/dataGridEditBusinessInterestCell';

/**
 * Buy and sell needs step
 */
const NeedsStep: EditFnaStepComponent = ({ fna, setValue }) => {
  const { processRowUpdate, parties, getRowId, checkIfCellIsEditable, deleteParty } =
    useFnaPartiesUpdater(fna.buyandsellParties);

  // Keep FNA buy and sell parties in sync with DataGrid changes
  useUpdateEffect(() => {
    setValue('buyandsellParties', parties);
  }, [parties]);

  const columns = useMemo<GridColDef<FnaBuyAndSellParty>[]>(
    () =>
      [
        {
          field: 'actions',
          type: 'actions',
          getActions: ({ row }: GridRenderCellParams) => [
            <DataGridDeleteAction key="delete" onClick={(): void => deleteParty(row)} />,
          ],
        },
        {
          field: 'partyRoleplayerName',
          headerName: 'Name',
          minWidth: 150,
        },
        {
          field: 'partyRoleplayerTaxStatusNote',
          headerName: 'Tax status',
          minWidth: 150,
        },
        {
          field: 'partyRoleplayerIDReg',
          headerName: 'ID / Registration number',
          minWidth: 150,
        },
        {
          field: 'representativeRoleplayerName',
          headerName: 'Representative life',
          minWidth: 150,
        },
        makeNumberColumn({
          field: 'interest',
          headerName: 'Business Interest',
          valueFormatter: (value) => formatNumber(value, undefined, ' %'),
          renderEditCell: DataGridEditBusinessInterestCell,
        }),
        makeNumberColumn({
          field: 'loanaccount',
          headerName: 'Value of loan accounts',
        }),
        makeAsyncOptionsColumn({
          field: 'capacityID',
          headerName: 'Capacity',
          optionsEndpoint: '/Capacitys',
          excludeOptionIds:
            fna.buyandsell?.policyStructureID === FnaPolicyStructure.ShareBuyBack
              ? [NeedCapacityType.Buy, NeedCapacityType.BuyingAndSelling]
              : [],
          processRowUpdate,
        }),
        makeBooleanColumn({
          field: 'marriageTypeID',
          headerName: 'Married in COP',
          processRowUpdate,
          getCurrentBooleanValue: (value) => value === MarriageType.Community,
          getFieldValueFromBoolean: (value) =>
            value ? MarriageType.Community : MarriageType.Unknown,
        }),
        makeNumberColumn({
          field: 'existingprovision',
          headerName: 'Existing provision',
        }),
        ...makeEstateDutyColumns(processRowUpdate),
        fna.buyandsell?.policyStructureID === FnaPolicyStructure.ShareBuyBack &&
          makeBooleanColumn({
            field: 'dividendWithholdingTax',
            headerName: 'Dividend Witholding Tax',
            processRowUpdate,
          }),
        makeBooleanColumn({
          field: 'disabilityBenefit',
          headerName: 'Disability benefit',
          processRowUpdate,
        }),
      ].filter(Boolean) as GridColDef<FnaBuyAndSellParty>[],
    [processRowUpdate, fna.buyandsell?.policyStructureID, deleteParty],
  );

  return (
    <DataGrid
      actionsVisibleOnHover={false}
      className="mt-12"
      rows={parties}
      columns={columns}
      enableQuickFilter={false}
      getRowId={getRowId}
      isCellEditable={checkIfCellIsEditable}
      processRowUpdate={processRowUpdate}
    />
  );
};

export default NeedsStep;
